// =============================================================================
// CONTENT VALIDATION
// =============================================================================
// This file provides helper functions to validate content JSON files
// against our Zod schemas. It's used at build time to catch errors
// BEFORE deploying a broken site.
//
// HOW IT WORKS:
// 1. You import the validate function
// 2. Pass it the raw JSON data from a file
// 3. It either returns the validated data (with defaults filled in)
//    or throws a clear error explaining what's wrong
//
// EXAMPLE:
//   const rawJson = JSON.parse(fs.readFileSync('content/restaurant.json', 'utf-8'));
//   const restaurant = validateRestaurant(rawJson);
//   // restaurant is now fully typed and validated ✓
//   // If restaurant.json had a missing field, this would have thrown
//   // an error like: "Phone number is required at restaurant.phone"
// =============================================================================

import { type ZodError, type ZodType } from 'zod';
import { RestaurantSchema, type Restaurant } from './restaurant.schema.js';
import { MenuSchema, type Menu } from './menu.schema.js';
import { SiteConfigSchema, type SiteConfig } from './site-config.schema.js';

// -----------------------------------------------------------------------------
// Generic Validation Helper
// -----------------------------------------------------------------------------

/**
 * Validates raw data against a Zod schema.
 *
 * @param schema - The Zod schema to validate against
 * @param data - The raw data (usually parsed from a JSON file)
 * @param label - A human-readable label for error messages (e.g., "restaurant.json")
 * @returns The validated and typed data (with defaults filled in)
 * @throws Error with a clear message if validation fails
 */
function validate<T>(schema: ZodType<T, any, any>, data: unknown, label: string): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    const formattedErrors = formatZodErrors(result.error, label);
    throw new Error(
      `\n❌ Validation failed for ${label}:\n${formattedErrors}\n` +
        `Fix the errors above in your content file and try again.\n`,
    );
  }

  return result.data;
}

/**
 * Formats Zod validation errors into human-readable messages.
 * Instead of a raw Zod error object, the restaurant owner (or you)
 * sees clear messages like:
 *   - "restaurant.json → phone: Phone number is required"
 *   - "menu.json → items[2].price: Price cannot be negative"
 */
function formatZodErrors(error: ZodError, label: string): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.join('.');
      return `  • ${label} → ${path || '(root)'}: ${issue.message}`;
    })
    .join('\n');
}

// -----------------------------------------------------------------------------
// Typed Validation Functions
// -----------------------------------------------------------------------------

/**
 * Validate a restaurant.json file.
 * Returns fully typed Restaurant data with all defaults filled in.
 */
export function validateRestaurant(data: unknown): Restaurant {
  return validate(RestaurantSchema, data, 'restaurant.json');
}

/**
 * Validate a menu.json file.
 * Returns fully typed Menu data with all defaults filled in.
 */
export function validateMenu(data: unknown): Menu {
  return validate(MenuSchema, data, 'menu.json');
}

/**
 * Validate a site.config.ts configuration object.
 * Returns fully typed SiteConfig with all defaults filled in.
 */
export function validateSiteConfig(data: unknown): SiteConfig {
  return validate(SiteConfigSchema, data, 'site.config');
}
